import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, Target, Eye, Users } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Humanização',
      description: 'Tratamento acolhedor e personalizado para cada paciente.'
    },
    {
      icon: Target,
      title: 'Excelência',
      description: 'Busca constante pela qualidade em todos os nossos serviços.'
    },
    {
      icon: Eye,
      title: 'Transparência',
      description: 'Comunicação clara e honesta em todas as etapas do tratamento.'
    },
    {
      icon: Users,
      title: 'Colaboração',
      description: 'Trabalho em equipe multidisciplinar para melhores resultados.'
    }
  ];

  const certifications = [
    'CRP - Conselho Regional de Psicologia',
    'CRFa - Conselho Regional de Fonoaudiologia',
    'Certificação ISO 9001',
    'Membro da Sociedade Brasileira de Psicanálise',
    'Credenciamento junto aos principais convênios'
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header da seção */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            Sobre a Clínica
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Mais de 10 anos cuidando
            <span className="block text-primary">da sua saúde mental</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A AYMEE Clínica Multidisciplinar nasceu com o propósito de oferecer
            atendimento integral e humanizado, reunindo profissionais especializados 
            em um ambiente acolhedor e moderno.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Imagem da clínica */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="/images/clinic_reception_2.jpeg" 
                alt="Recepção da AYMEE Clínica Multidisciplinar"
                className="rounded-lg shadow-medium w-full h-64 object-cover"
              />
              <img 
                src="/images/therapy_room_1.jpeg" 
                alt="Sala de terapia"
                className="rounded-lg shadow-medium w-full h-64 object-cover mt-8"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-white rounded-lg p-6 shadow-strong">
              <div className="text-3xl font-bold text-primary">98%</div>
              <div className="text-sm text-gray-600">Satisfação dos Pacientes</div>
            </div>
          </div>

          {/* Conteúdo */}
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Nossa História e Missão
            </h3>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Fundada em 2014, a AYMEE Clínica Multidisciplinar surgiu da visão de criar um espaço
                onde diferentes especialidades da saúde mental pudessem trabalhar de 
                forma integrada, proporcionando um atendimento mais completo e eficaz.
              </p>
              <p>
                Nossa missão é promover o bem-estar mental e emocional de nossos 
                pacientes através de tratamentos baseados em evidências científicas, 
                sempre priorizando o acolhimento e a individualidade de cada pessoa.
              </p>
              <p>
                Acreditamos que a saúde mental é fundamental para uma vida plena e 
                trabalhamos incansavelmente para quebrar estigmas e tornar o 
                cuidado psicológico mais acessível e humanizado.
              </p>
            </div>

            {/* Certificações */}
            <div className="mt-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Certificações e Credenciamentos
              </h4>
              <div className="space-y-2">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-gray-600">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Valores */}
        <div>
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Nossos Valores
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-medium transition-smooth">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">
                    {value.title}
                  </h4>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Novo espaço para idosos - Destaque */}
        <div className="mt-16">
          <Card className="bg-gradient-secondary text-white overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <Badge variant="secondary" className="mb-4 bg-white/20 text-white border-white/30">
                    Novidade
                  </Badge>
                  <h3 className="text-3xl font-bold mb-4">
                    Novo Espaço para Atividades Motoras
                  </h3>
                  <p className="text-lg mb-6 text-white/90">
                    Inauguramos um espaço especialmente dedicado às atividades motoras 
                    para idosos, com equipamentos modernos e profissionais especializados 
                    em gerontologia.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <div className="bg-white/20 rounded-lg p-3">
                      <div className="font-semibold">Fisioterapia</div>
                      <div className="text-sm text-white/80">Especializada</div>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3">
                      <div className="font-semibold">Exercícios</div>
                      <div className="text-sm text-white/80">Adaptados</div>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3">
                      <div className="font-semibold">Ambiente</div>
                      <div className="text-sm text-white/80">Seguro</div>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="/images/therapy_room_3.jpeg" 
                    alt="Novo espaço para atividades motoras"
                    className="rounded-lg shadow-strong w-full h-64 object-cover"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default About;